import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { globals } from "../globals";
import { Financiamento } from "../models/financiamento/financiamento";
import { Financiadora } from "../models/financiamento/financiadora";
import { IntegradorFinanciadoraSimples } from "../models/financiamento/integrador-financiadora-simples";
import { Observable } from "rxjs";
import { FinanciadoraModel } from "../dominio/financiadora-model";
import { PaginatedModel } from "../dominio/paginated-model";
import { IGridifyQuery } from "gridify-client";
import { QueryFilterService } from "../core/services/query-filter-service";
import { FinanciadoraOctopusModel } from "../dominio/financiadora-octopus-model";
import { HubConfigSchemaModel } from "../dominio/hub-config-schema-model";
import { IntegracaoOctopusModel } from "../dominio/integracao-octopus-model";
import { CamposAdicionaisConfigFinanciamento, ConfigFinanciamentoModel } from "../dominio/config-financiamento-model";
import { GiFinSituacaoModel } from "../dominio/gi-fin-situacao-model";
import { FinanciadoraIntegradorModel } from "../financiamento/models/financiadora-integrador-model";

export class FinanciadorasResponse {
  results: FinanciadoraModel[];
  pagination: PaginatedModel;
}

export class DetalheFinanciadoraResponse {
  result: FinanciadoraModel;
}

export class CadastrarFinanciadoraRequest {
  nome: string;
  gateway?: string | null;
  ativo: boolean;
  funilMonitorProposta?: number | null;
  carencias: number[];
  parcelas: number[];
  celulaCreditoPodeAlterarConfig: boolean;
  somentePropostasComSeguro: boolean;
  podeEnviarPropostaManual: boolean;
  propostasExpiramNoMesmoDia: boolean;
  tempoExpiracaoProposta: number;
  urlTermosDeUso?: string;
  urlPoliticaPrivacidade?: string;
  idFormulario?: number;
  arquivoImagem: File;
}

export class ConfigOctopusResponse {
  results: FinanciadoraOctopusModel[];
  pagination: PaginatedModel;
}

export class ListarCategoriasFinanciadoraResponse {
  results: FinanciadoraOctopusModel[];
  pagination: PaginatedModel;
}

export class ConfigHubResponse {
  result: HubConfigSchemaModel;
}

export class ListarGatewayResponse {
  results: IntegracaoOctopusModel[];
  pagination: PaginatedModel;
}

export class SaveConfigRequest {
  configModel: string;
}

export class DetalheConfigResponse {
  result: ConfigFinanciamentoModel;
}

export class AtualizarConfigRequest {
  id: number;
  ativo: boolean;
  usaControleTarefas: boolean;
  valorMin: number;
  valorMax: number;
  ordemSelecao: number;
  termo: string;
  usaTermo: boolean;
  validaExpiracaoProposta: boolean;
  envioCelulaSimulacao: number[];
  usaMonitorProposta: boolean;
  funilMonitorProposta: number;
  usaFunilPorFinanciadora: boolean;
  liberarPropostasAutomaticamente: boolean;
  camposObrigatorios: string[];
  numeroWhatsapp: string;
  camposAdicionais?: CamposAdicionaisConfigFinanciamento;
}

export class ListaSituacoesResponse {
  results: GiFinSituacaoModel[];
  pagination: PaginatedModel;
}

export class AtualizaSituacaoRequest {
  descricaoPersonalizada?: string | null;
  corPersonalizada?: string | null;
}

export class ConfigResponse {
  result: Financiamento;
}

export class ListaFinanciadoraIntegradorResponse {
  results?: FinanciadoraIntegradorModel[];
  pagination?: PaginatedModel;
}

@Injectable({
  providedIn: "root",
})
export class FinanciamentoService {
  constructor(private http: HttpClient, private queryFilterService: QueryFilterService) {}

  atualizar(financiamento: Financiamento) {
    return this.http.post(globals.apiUrl + "/api/Financiamento/Salvar", financiamento);
  }

  buscar(): Observable<ConfigResponse> {
    return this.http.get<ConfigResponse>(globals.apiUrl + "/api/v2/financiamentos/resources/config");
  }

  consultarFinanciadoras(ativo) {
    return this.http.get<FinanciadoraModel[]>(globals.apiUrl + "/api/Financiamento/FinanciadorasAtivas", {
      params: new HttpParams().set("ativo", ativo),
    });
  }

  consultarFinanciadorasParaIntegrador(idIntegrador, mostrarTodas) {
    return this.http.get<IntegradorFinanciadoraSimples[]>(
      globals.apiUrl + "/api/Financiamento/BuscarFinanciadorasParaIntegrador",
      { params: new HttpParams().set("idIntegrador", idIntegrador).set("mostrarTodas", mostrarTodas) }
    );
  }

  buscarFinanciadoraV2(id: number): Observable<DetalheFinanciadoraResponse> {
    return this.http.get<DetalheFinanciadoraResponse>(
      `${globals.apiUrl}/api/v2/financiamentos/resources/financiadoras/${id}`
    );
  }

  buscarFinanciadoras(queryFilter: IGridifyQuery | null = null): Observable<FinanciadorasResponse> {
    const params = this.queryFilterService.getQueryParams(queryFilter);

    return this.http.get<FinanciadorasResponse>(`${globals.apiUrl}/api/v2/financiamentos/resources/financiadoras`, {
      params: params,
    });
  }

  cadastrarFinanciadora(financiadoraRequest) {
    return this.http.post(`${globals.apiUrl}/api/v2/financiamentos/resources/financiadoras`, financiadoraRequest, {});
  }

  atualizarFinanciadora(idFinanciadora: number, financiadoraRequest) {
    return this.http.put(
      `${globals.apiUrl}/api/v2/financiamentos/resources/financiadora/${idFinanciadora}`,
      financiadoraRequest,
      {}
    );
  }

  atualizarOrdemFinanciadora(idFinanciadora: number, ordem: number) {
    return this.http.put(
      `${globals.apiUrl}/api/v2/financiamentos/resources/financiadoras/${idFinanciadora}/ordem`,
      { ordem },
      {}
    );
  }

  buscarCategoriasFinanciadora(idFinanciadora: number): Observable<ListarCategoriasFinanciadoraResponse> {
    return this.http.get<ListarCategoriasFinanciadoraResponse>(
      `${globals.apiUrl}/api/v2/financiamentos/resources/financiadoras/${idFinanciadora}/config`
    );
  }

  buscarGateway(): Observable<ListarGatewayResponse> {
    return this.http.get<ListarGatewayResponse>(`${globals.apiUrl}/api/v2/financiamentos/resources/gateways`);
  }

  buscarConfigHub(idFinanciadora: number, idCategoria: number): Observable<ConfigHubResponse> {
    return this.http.get<ConfigHubResponse>(
      `${globals.apiUrl}/api/v2/financiamentos/resources/financiadoras/${idFinanciadora}/config/${idCategoria}/schema`
    );
  }

  atualizaConfigHub(idFinanciadora: number, idCategoria: number, configHubRequest: SaveConfigRequest) {
    return this.http.patch(
      `${globals.apiUrl}/api/v2/financiamentos/resources/financiadoras/${idFinanciadora}/config/${idCategoria}`,
      configHubRequest,
      {}
    );
  }

  listarSituacoesFinanciamento(): Observable<ListaSituacoesResponse> {
    return this.http.get<ListaSituacoesResponse>(`${globals.apiUrl}/api/v2/financiamentos/resources/situacoes`);
  }

  atualizarSituacao(data: AtualizaSituacaoRequest, idSituacao: number) {
    return this.http.put(`${globals.apiUrl}/api/v2/financiamentos/resources/situacoes/${idSituacao}`, data, {});
  }

  salvar(financiadora: Financiadora) {
    return this.http.post(globals.apiUrl + "/api/Financiamento/SalvarFinanciadora", financiadora, {
      responseType: "text",
    });
  }

  buscarTermosFinanciadora(idVinculo) {
    return this.http.get<any>(globals.apiUrl + "/api/Financiamento/BuscarTermosFinanciadora", {
      params: new HttpParams().set("idVinculo", idVinculo),
    });
  }

  buscarConfiguracoes(): Observable<DetalheConfigResponse> {
    return this.http.get<DetalheConfigResponse>(`${globals.apiUrl}/api/v2/financiamentos/resources/config`);
  }

  salvarConfiguracoes(data: AtualizarConfigRequest) {
    return this.http.put(`${globals.apiUrl}/api/v2/financiamentos/resources/config`, data);
  }
}
